/**
 * Super Admin Workspaces Page - 워크스페이스 목록 및 구독 관리
 * 
 * 모든 워크스페이스를 조회하고 개별 워크스페이스의 구독을 관리
 */

import { Trans, t } from '@lingui/macro';
import {
    Building2, Search, Filter, MoreHorizontal, CreditCard,
    FileText, Eye, ChevronRight, Clock, CheckCircle, XCircle, AlertCircle, Loader2
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import { WorkspaceTier } from '../../models/local/Types';
import { SubscriptionStatus } from '../../models/SubscriptionData';
import { Fire } from '../../services/FireClient';
import { Workspace } from '../../models/Workspace';

// 화면에 표시할 워크스페이스 타입
interface WorkspaceListItem {
    id: string;
    name: string;
    tier: WorkspaceTier;
    subscriptionStatus: SubscriptionStatus;
    memberCount: number;
    storageMB: number;
    endDate?: Date;
    billingEmail?: string;
    created?: Date;
}

// Mock Data - 참고용으로 유지
const MOCK_WORKSPACES: WorkspaceListItem[] = [
    { id: 'ws1', name: '株式会社サンプル', tier: 'PREMIUM', subscriptionStatus: 'active', memberCount: 25, storageMB: 45000, endDate: new Date('2026-03-15'), billingEmail: 'admin@sample.co.jp' },
    { id: 'ws2', name: 'テスト工房', tier: 'BASIC', subscriptionStatus: 'active', memberCount: 5, storageMB: 8000, endDate: new Date('2026-02-01'), billingEmail: 'billing@test.jp' },
    { id: 'ws3', name: 'デモ会社', tier: 'FREE', subscriptionStatus: 'expired', memberCount: 2, storageMB: 500 },
    { id: 'ws4', name: 'サブスク期限切れ', tier: 'PREMIUM', subscriptionStatus: 'past_due', memberCount: 10, storageMB: 22000, endDate: new Date('2025-12-31'), billingEmail: 'pay@overdue.jp' },
];

const tierColors: Record<WorkspaceTier, string> = {
    FREE: 'bg-slate-100 text-slate-700',
    BASIC: 'bg-blue-100 text-blue-700',
    STANDARD: 'bg-purple-100 text-purple-700',
    PREMIUM: 'bg-indigo-100 text-indigo-700',
    PREMIUM_TRIAL: 'bg-amber-100 text-amber-700',
    PREMIUM_PARTNER: 'bg-emerald-100 text-emerald-700',
};

const statusConfig: Record<SubscriptionStatus, { icon: React.ElementType; color: string; label: string }> = {
    active: { icon: CheckCircle, color: 'text-emerald-600', label: 'Active' },
    trial: { icon: Clock, color: 'text-amber-600', label: 'Trial' },
    past_due: { icon: AlertCircle, color: 'text-red-600', label: 'Past Due' },
    canceled: { icon: XCircle, color: 'text-slate-500', label: 'Canceled' },
    expired: { icon: XCircle, color: 'text-slate-400', label: 'Expired' },
};

// Workspace 모델을 UI용 데이터로 변환
function workspaceToListItem(ws: Workspace, memberCount: number = 0): WorkspaceListItem {
    return {
        id: ws.id,
        name: ws.name,
        tier: ws.tier || 'FREE',
        subscriptionStatus: 'active', // TODO: 실제 구독 상태 가져오기
        memberCount: memberCount,
        storageMB: 0, // TODO: 실제 스토리지 사용량 가져오기
        created: ws.created,
    };
}

const SuperAdminWorkspaces: React.FC = () => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [tierFilter, setTierFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [workspaces, setWorkspaces] = useState<WorkspaceListItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // 실제 데이터 로드
    useEffect(() => {
        const loadWorkspaces = async () => {
            try {
                setIsLoading(true);
                const allWorkspaces = await Fire.ws.getAllWorkspaces();
                const { collection, getDocs, doc, getDoc } = await import('firebase/firestore');

                // 각 워크스페이스의 멤버 수와 구독 정보를 가져옴 (성능을 위해 병렬 처리)
                const workspaceItems = await Promise.all(
                    allWorkspaces.map(async (ws) => {
                        try {
                            // 멤버 수 조회
                            const members = await Fire.ws.getWorkspaceMembers(ws.id);

                            // 구독 정보 조회
                            let tier: WorkspaceTier = ws.tier || 'FREE';
                            let subscriptionStatus: SubscriptionStatus = 'active';
                            let endDate: Date | undefined;

                            try {
                                const subDoc = await getDoc(doc(Fire.store, 'workspaces', ws.id, 'subscription', 'current'));
                                if (subDoc.exists()) {
                                    const subData = subDoc.data();
                                    tier = subData.planId || tier; // subscription의 planId가 있으면 우선
                                    subscriptionStatus = subData.status || 'active';
                                    endDate = subData.endDate?.toDate?.();
                                }
                            } catch (subErr) {
                                console.error(`Failed to load subscription for ${ws.id}:`, subErr);
                            }

                            return {
                                id: ws.id,
                                name: ws.name,
                                tier,
                                subscriptionStatus,
                                memberCount: members.length,
                                storageMB: 0,
                                endDate,
                                created: ws.created,
                            } as WorkspaceListItem;
                        } catch {
                            return {
                                id: ws.id,
                                name: ws.name,
                                tier: ws.tier || 'FREE',
                                subscriptionStatus: 'active' as SubscriptionStatus,
                                memberCount: 0,
                                storageMB: 0,
                                created: ws.created,
                            } as WorkspaceListItem;
                        }
                    })
                );

                setWorkspaces(workspaceItems);
            } catch (error) {
                console.error('Failed to load workspaces:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadWorkspaces();
    }, []);

    const filteredWorkspaces = workspaces.filter(ws => {
        const matchesSearch = ws.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            ws.billingEmail?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesTier = tierFilter === 'all' || ws.tier === tierFilter;
        const matchesStatus = statusFilter === 'all' || ws.subscriptionStatus === statusFilter;
        return matchesSearch && matchesTier && matchesStatus;
    });

    const formatDate = (date?: Date) => {
        if (!date) return '-';
        const d = date instanceof Date ? date : new Date(date);
        return d.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
    };

    const formatStorage = (mb: number) => {
        if (mb >= 1000) return `${(mb / 1000).toFixed(1)} GB`;
        return `${mb} MB`;
    };

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-md border-b border-slate-100/50 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-bold text-slate-900"><Trans>Workspaces</Trans></h1>
                        <p className="text-sm text-slate-500 mt-0.5">
                            <Trans>Manage workspace subscriptions and billing</Trans>
                        </p>
                    </div>
                    <Button className="bg-indigo-600 hover:bg-indigo-700">
                        <CreditCard className="w-4 h-4 mr-2" />
                        <Trans>Grant Subscription</Trans>
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white border-b border-slate-100/50 px-6 py-3">
                <div className="flex items-center gap-4">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            className="pl-9 bg-slate-50 border-slate-200"
                            placeholder={t`Search by name or email...`}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <Select value={tierFilter} onValueChange={setTierFilter}>
                        <SelectTrigger className="w-[140px]">
                            <SelectValue placeholder={t`Tier`} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all"><Trans>All Tiers</Trans></SelectItem>
                            <SelectItem value="FREE">Free</SelectItem>
                            <SelectItem value="BASIC">Basic</SelectItem>
                            <SelectItem value="PREMIUM">Premium</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="w-[140px]">
                            <SelectValue placeholder={t`Status`} />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all"><Trans>All Status</Trans></SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="past_due">Past Due</SelectItem>
                            <SelectItem value="expired">Expired</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6 bg-slate-50/30">
                {/* Loading State */}
                {isLoading && (
                    <div className="flex flex-col items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mb-3" />
                        <p className="text-slate-500"><Trans>Loading workspaces...</Trans></p>
                    </div>
                )}

                {/* Workspace List */}
                {!isLoading && (
                    <div className="space-y-3">
                        {/* Stats */}
                        <div className="text-sm text-slate-500 mb-4">
                            <Trans>Total: {filteredWorkspaces.length} workspaces</Trans>
                        </div>

                        {filteredWorkspaces.map((ws) => {
                            const status = statusConfig[ws.subscriptionStatus];
                            const StatusIcon = status.icon;

                            return (
                                <Card
                                    key={ws.id}
                                    className="hover:shadow-md transition-all cursor-pointer border-slate-200/60"
                                    onClick={() => navigate(`/super-admin/workspaces/${ws.id}`)}
                                >
                                    <CardContent className="p-4">
                                        <div className="flex items-center gap-4">
                                            {/* Icon */}
                                            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                                                <Building2 className="w-5 h-5 text-slate-600" />
                                            </div>

                                            {/* Info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold text-slate-900 truncate">{ws.name}</span>
                                                    <Badge className={cn('text-[10px] font-medium', tierColors[ws.tier])}>
                                                        {ws.tier}
                                                    </Badge>
                                                </div>
                                                <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                                                    <span>{ws.memberCount} members</span>
                                                    {ws.storageMB > 0 && <span>{formatStorage(ws.storageMB)}</span>}
                                                    {ws.created && <span>Created: {formatDate(ws.created)}</span>}
                                                    {ws.billingEmail && <span>{ws.billingEmail}</span>}
                                                </div>
                                            </div>

                                            {/* Status & Date */}
                                            <div className="text-right shrink-0">
                                                <div className={cn('flex items-center gap-1 text-sm font-medium', status.color)}>
                                                    <StatusIcon className="w-4 h-4" />
                                                    <span>{status.label}</span>
                                                </div>
                                                <div className="text-xs text-slate-400 mt-1">
                                                    {ws.endDate ? `Expires: ${formatDate(ws.endDate)}` : '-'}
                                                </div>
                                            </div>

                                            {/* Actions */}
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                                    <Button variant="ghost" size="icon" className="shrink-0">
                                                        <MoreHorizontal className="w-4 h-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/super-admin/workspaces/${ws.id}`); }}>
                                                        <Eye className="w-4 h-4 mr-2" />
                                                        <Trans>View Details</Trans>
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={(e) => e.stopPropagation()}>
                                                        <CreditCard className="w-4 h-4 mr-2" />
                                                        <Trans>Manage Subscription</Trans>
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={(e) => e.stopPropagation()}>
                                                        <FileText className="w-4 h-4 mr-2" />
                                                        <Trans>Create Invoice</Trans>
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>

                                            <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}

                        {filteredWorkspaces.length === 0 && (
                            <div className="text-center py-12 text-slate-500">
                                <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                                <p><Trans>No workspaces found</Trans></p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SuperAdminWorkspaces;
